import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, InfoIcon, SendHorizontalIcon, UserPlus2Icon, ChevronDownIcon, RefreshCwIcon, FileTextIcon } from 'lucide-react';
import { useWhatsAppMessages } from '../hooks/useWhatsAppMessages';
import { useWhatsAppChats, useMarkChatAsRead } from '../hooks/useWhatsAppChats';
import { useWhatsAppStatus } from '../hooks/useWhatsAppStatus';
import { useWhatsAppStore } from '../stores/whatsappStore';
import { useClients } from '../hooks/useClients';
import { useLeadLabelMap, useLeadLabelMutation } from '../hooks/useLeadLabels';
import type { LeadLabel } from '../types/leads';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import { api } from '../lib/apiClient';
import { LeadConversionModal } from '../components/LeadConversionModal';
import { useConvertLead } from '../hooks/useConvertLead';
import { ImageViewer } from '../components/whatsapp/ImageViewer';
import { fetchAuthenticatedMedia, sendReaction } from '../api/whatsapp';
import { LinkifiedText } from '../components/whatsapp/LinkifiedText';
import { AddNoteModal } from '../components/AddNoteModal';
import { TemplatePickerModal } from '../components/templates/TemplatePickerModal';
import { whatsappJidToPhone } from '../../lib/whatsapp/jid';

type ClientActivityStatus = 'active' | 'past';

const clientStatusOptions: { value: ClientActivityStatus; label: string }[] = [
  { value: 'active', label: 'Active Client' },
  { value: 'past', label: 'Inactive Client' },
];

const leadLabelOptions: { value: LeadLabel; label: string }[] = [
  { value: 'hot', label: 'Hot' },
  { value: 'warm', label: 'Warm' },
  { value: 'cold', label: 'Cold' },
  { value: 'new', label: 'New' },
];

const leadLabelActiveStyles: Record<LeadLabel, string> = {
  hot: 'bg-red-500 text-white border border-red-400 shadow',
  warm: 'bg-orange-400 text-white border border-orange-300 shadow',
  cold: 'bg-blue-500 text-white border border-blue-400 shadow',
  new: 'bg-white text-[#5B8E87] border border-white shadow',
};

function mapClientStatus(status: string | null | undefined): ClientActivityStatus | null {
  if (!status) return null;
  return status === 'active' ? 'active' : 'past';
}

export function WhatsAppChatScreen() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const jid = params.jid ? decodeURIComponent(params.jid) : undefined;
  const { data: chatsData } = useWhatsAppChats();
  const status = useWhatsAppStatus();
  const clientsQuery = useClients();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const leadLabelMutation = useLeadLabelMutation();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const unreadCountRef = useRef<number | null>(null);
  const hasScrolledToUnread = useRef(false);

  // Use Zustand store for composer text
  const composerText = useWhatsAppStore((state) => state.composerText);
  const setComposerText = useWhatsAppStore((state) => state.setComposerText);
  const draft = composerText;
  const setDraft = setComposerText;
  const [clientStatusOverride, setClientStatusOverride] = useState<ClientActivityStatus | null>(null);
  const [leadLabelOverride, setLeadLabelOverride] = useState<LeadLabel | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [leadLabelError, setLeadLabelError] = useState<string | null>(null);
  const [showConversionModal, setShowConversionModal] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const convertLeadMutation = useConvertLead();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());
  const [imageBlobUrls, setImageBlobUrls] = useState<Map<string, string>>(new Map());
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [selectedMessageForNote, setSelectedMessageForNote] = useState<{
    id: string;
    text: string;
    timestamp: string;
    media_url?: string;
    media_type?: string;
  } | null>(null);
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null); // messageId of message showing reaction picker
  const quickReactions = ['❤️', '👍', '😂', '😮', '😢', '🙏'];

  // Auto-growing textarea refs and state
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const chatContactName = useMemo(() => {
    const entry = chatsData?.chats.find((chat) => chat.jid === jid);
    return entry?.name || 'WhatsApp Contact';
  }, [chatsData?.chats, jid]);

  // Capture unread count before marking as read
  useEffect(() => {
    if (jid && chatsData?.chats) {
      const chat = chatsData.chats.find((chat) => chat.jid === jid);
      if (chat && unreadCountRef.current === null) {
        unreadCountRef.current = chat.unreadCount || 0;
      }
    }
  }, [jid, chatsData?.chats]);

  const state = (location.state ?? {}) as {
    clientId?: string;
    clientName?: string;
    clientPhone?: string;
    from?: string;
    chatsActiveTab?: 'all' | 'unread' | 'followups';
    chatsAudienceTab?: 'leads' | 'clients';
    returnTo?: {
      path: string;
      tab?: string;
      filter?: string;
      timeTab?: string;
      clientFilter?: string;
    };
  };
  const cameFromChats = state.from === 'chats';
  const cameFromSchedule = state.from === 'schedule';
  const fallbackClientRecord = useMemo(() => {
    if (!clientsQuery.data || clientsQuery.data.length === 0) return undefined;
    if (state.clientId) {
      const byId = clientsQuery.data.find((client) => client.id === state.clientId);
      if (byId) return byId;
    }
    if (jid) {
      const normalizedJid = jid.toLowerCase();
      return clientsQuery.data.find(
        (client) => client.whatsapp_jid?.toLowerCase() === normalizedJid,
      );
    }
    return undefined;
  }, [clientsQuery.data, jid, state.clientId]);
  const resolvedClientId = state.clientId ?? fallbackClientRecord?.id;
  // Prefer client name if a client is linked, otherwise use state name or chat contact name
  const preferredName = fallbackClientRecord?.name || state.clientName || chatContactName;
  // Get WhatsApp phone number from client record or extract from JID
  const displayPhone = useMemo(() => {
    return fallbackClientRecord?.phone || (jid ? whatsappJidToPhone(jid) : null);
  }, [fallbackClientRecord?.phone, jid]);
  const leadLabelQuery = useLeadLabelMap(Boolean(!resolvedClientId && jid));
  const currentLeadLabel = leadLabelQuery.map.get(jid ?? '')?.label ?? 'new';
  useEffect(() => {
    setLeadLabelOverride(null);
  }, [currentLeadLabel]);
  const baseClientStatus = mapClientStatus(
    fallbackClientRecord?.status ?? null,
  );
  useEffect(() => {
    setClientStatusOverride(null);
  }, [baseClientStatus]);
  const clientStatusMutation = useMutation({
    mutationFn: async (nextStatus: ClientActivityStatus) => {
      if (!resolvedClientId) {
        throw new Error('Client not available');
      }
      const response = await apiRequest(`/api/clients/${resolvedClientId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      }, session?.access_token);
      return response;
    },
    onSuccess: (_, nextStatus) => {
      if (resolvedClientId) {
        queryClient.invalidateQueries({ queryKey: ['client', resolvedClientId] });
      }
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setClientStatusOverride(nextStatus);
      setStatusError(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Unable to update status';
      setStatusError(message);
    },
  });
  const leadTagLoading = leadLabelQuery.isLoading;

  const { data, isLoading, sendMessage } = useWhatsAppMessages(jid, {
    contactName: preferredName,
  });
  const markAsReadMutation = useMarkChatAsRead();

  // Manual sync mutation - fetches recent messages for current chat from Evolution API
  // NOTE: This only syncs the CURRENT chat, not all chats
  // Fetches last 100 messages from Evolution API's in-memory store
  const manualSyncMutation = useMutation({
    mutationFn: async () => {
      if (!jid) throw new Error('Missing chat id');
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`http://localhost:3001/api/whatsapp/chats/${encodeURIComponent(jid)}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error?.message || 'Sync failed');
      }

      return await response.json();
    },
    onSuccess: () => {
      // Refetch messages after sync completes
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages', jid] });
    },
  });

  const messages = data?.messages ?? [];
  const prevMessagesLengthRef = useRef<number>(0);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    // Only scroll if messages have been added (not on initial render when length is 0)
    if (messages.length > 0 && messages.length !== prevMessagesLengthRef.current) {
      const isInitialLoad = prevMessagesLengthRef.current === 0;

      // For initial load, use instant scroll to avoid race conditions with image loading
      // For new messages, use smooth scroll
      messagesEndRef.current?.scrollIntoView({
        behavior: isInitialLoad ? 'instant' : 'smooth'
      });

      prevMessagesLengthRef.current = messages.length;
    }
  }, [messages.length]);

  // Mark chat as read when opening the chat
  useEffect(() => {
    if (jid) {
      markAsReadMutation.mutate(jid);
    }
  }, [jid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-grow textarea height calculation
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (capped at max)
    const maxHeight = 144; // 6 lines * 24px line-height
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${newHeight}px`;

    // Update scrollable state for gradient indicator
    setIsScrollable(textarea.scrollHeight > maxHeight);
  }, [draft]);

  const handleSend = () => {
    if (!draft.trim() || !jid) return;
    sendMessage.mutate({ text: draft.trim() });
    setDraft('');
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    try {
      console.log('Sending reaction:', { messageId, emoji });
      await sendReaction(messageId, emoji);
      setShowReactionPicker(null);
      // Refetch messages to update reactions
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'messages', jid] });
    } catch (error) {
      console.error('Failed to send reaction:', error);
      // Show error to user
      alert(`Failed to send reaction: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  const displayedClientStatus = clientStatusOverride ?? baseClientStatus;
  const showClientStatusControls = Boolean(resolvedClientId);
  const showLeadTagControls = !resolvedClientId && !clientsQuery.isLoading;
  const activeLeadLabel = leadLabelOverride ?? currentLeadLabel;
  const leadControlsDisabled = leadTagLoading || leadLabelMutation.isPending;
  const clientControlsDisabled = clientStatusMutation.isPending;

  const handleClientStatusChange = async (nextStatus: ClientActivityStatus) => {
    if (!resolvedClientId) return;
    if (clientControlsDisabled) return;
    if (nextStatus === baseClientStatus && !clientStatusOverride) return;
    setStatusError(null);
    setClientStatusOverride(nextStatus);
    try {
      await clientStatusMutation.mutateAsync(nextStatus);
    } catch {
      setClientStatusOverride(null);
    }
  };

  const handleLeadLabelChange = async (nextLabel: LeadLabel) => {
    if (!jid) return;
    if (leadControlsDisabled) return;
    if (nextLabel === currentLeadLabel && !leadLabelOverride) return;
    setLeadLabelError(null);
    setLeadLabelOverride(nextLabel);
    try {
      await leadLabelMutation.mutateAsync({ jid, label: nextLabel });
    } catch (error) {
      setLeadLabelOverride(null);
      const message = error instanceof Error ? error.message : 'Unable to update lead tag';
      setLeadLabelError(message);
    }
  };

  // Fetch authenticated images and convert to blob URLs
  useEffect(() => {
    const fetchImages = async () => {
      for (const message of messages) {
        if (message.mediaUrl && message.mediaType === 'image' && !imageBlobUrls.has(message.id) && !imageLoadErrors.has(message.id)) {
          try {
            const blobUrl = await fetchAuthenticatedMedia(message.mediaUrl);
            setImageBlobUrls(prev => new Map(prev).set(message.id, blobUrl));

            // After image loads, scroll to bottom if this was the initial load
            // This ensures we stay at the bottom even after images cause layout shifts
            if (prevMessagesLengthRef.current > 0) {
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
              }, 50);
            }
          } catch (error) {
            console.error('Failed to fetch image:', error);
            setImageLoadErrors(prev => new Set(prev).add(message.id));
          }
        }
      }
    };

    fetchImages();

    // Cleanup: revoke blob URLs when component unmounts
    return () => {
      imageBlobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeConversionModal = () => {
    setShowConversionModal(false);
    setConversionError(null);
  };

  const handleConvertLead = async (payload: { name: string; phone: string }) => {
    if (!jid) return;
    setConversionError(null);
    try {
      const response = await convertLeadMutation.mutateAsync({
        name: payload.name,
        phone: payload.phone,
        whatsappJid: jid,
      });
      const clientId = response.client?.id;
      if (!clientId) {
        setConversionError(
          response.message || 'Unable to convert lead to client',
        );
        return;
      }
      const activeLabel = currentLeadLabel;
      await leadLabelMutation.mutateAsync({
        jid,
        convertedClientId: clientId,
        label: activeLabel,
      });
      closeConversionModal();
      // Navigate back to chats with preserved tab state
      const params = new URLSearchParams();
      if (state.chatsActiveTab) params.set('activeTab', state.chatsActiveTab);
      if (state.chatsAudienceTab) params.set('audienceTab', state.chatsAudienceTab);
      const queryString = params.toString();
      navigate(`/chats${queryString ? `?${queryString}` : ''}`);
    } catch (error) {
      setConversionError(
        error instanceof Error ? error.message : 'Unable to convert lead',
      );
    }
  };

  const composerDisabled = !status.data?.connected;

  // Group messages by date for WhatsApp-style date dividers
  const groupedMessages = useMemo(() => {
    const groups: { date: Date; dateLabel: string; messages: typeof messages }[] = [];

    messages.forEach((message) => {
      // Skip messages with no timestamp
      if (!message.timestamp) {
        console.warn('Message missing timestamp, skipping:', message.id);
        return;
      }

      const msgDate = new Date(message.timestamp);

      // Check if date is valid
      if (isNaN(msgDate.getTime())) {
        console.warn('Invalid message timestamp:', message.timestamp, 'for message:', message.id);
        return; // Skip messages with invalid dates
      }

      const dateKey = msgDate.toDateString();

      let group = groups.find((g) => g.date.toDateString() === dateKey);

      if (!group) {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        let dateLabel: string;
        if (msgDate.toDateString() === today.toDateString()) {
          dateLabel = 'Today';
        } else if (msgDate.toDateString() === yesterday.toDateString()) {
          dateLabel = 'Yesterday';
        } else {
          dateLabel = msgDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: msgDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
          });
        }

        group = { date: msgDate, dateLabel, messages: [] };
        groups.push(group);
      }

      group.messages.push(message);
    });

    // Sort groups by date ascending
    return groups.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [messages]);

  const handleBack = () => {
    if (cameFromChats) {
      // Preserve tab state when navigating back to chats
      const params = new URLSearchParams();
      if (state.chatsActiveTab) params.set('activeTab', state.chatsActiveTab);
      if (state.chatsAudienceTab) params.set('audienceTab', state.chatsAudienceTab);
      const queryString = params.toString();
      navigate(`/chats${queryString ? `?${queryString}` : ''}`, { replace: true });
    } else if (cameFromSchedule && state.returnTo) {
      // Preserve state when navigating back to schedule
      const params = new URLSearchParams();
      if (state.returnTo.tab) params.set('tab', state.returnTo.tab);
      if (state.returnTo.filter) params.set('filter', state.returnTo.filter);
      if (state.returnTo.timeTab) params.set('timeTab', state.returnTo.timeTab);
      if (state.returnTo.clientFilter && state.returnTo.clientFilter !== 'all') {
        params.set('client', state.returnTo.clientFilter);
      }
      const queryString = params.toString();
      navigate(`${state.returnTo.path}${queryString ? `?${queryString}` : ''}`, { replace: true });
    } else {
      // Fallback to dashboard to avoid history loop
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="h-screen bg-[#ECE5DD] flex flex-col overflow-hidden">
      <header className="bg-[#5B8E87] text-white p-4 flex flex-col gap-3 flex-shrink-0 z-10 shadow">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-2 -ml-2">
            <ArrowLeftIcon className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <p className="font-semibold">{preferredName}</p>
            {displayPhone && (
              <p className="text-xs text-white/70">{displayPhone}</p>
            )}
            <p className="text-xs opacity-80">
              {status.data?.connected ? 'WhatsApp Connected' : 'WhatsApp Disconnected'}
            </p>
          </div>

          {/* Sync button */}
          <button
            type="button"
            onClick={() => manualSyncMutation.mutate()}
            disabled={manualSyncMutation.isPending}
            className={`p-2 rounded-lg transition-colors ${
              manualSyncMutation.isPending
                ? 'bg-white/10 text-white/60 cursor-not-allowed'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            title="Sync messages from WhatsApp"
          >
            <RefreshCwIcon className={`w-5 h-5 ${manualSyncMutation.isPending ? 'animate-spin' : ''}`} />
          </button>

          {resolvedClientId ? (
            <button
              type="button"
              onClick={() => navigate(`/client/${resolvedClientId}`)}
              className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
              title="View Client Profile"
            >
              <InfoIcon className="w-5 h-5" />
            </button>
          ) : (
            showLeadTagControls && jid && (
              <button
                type="button"
                onClick={() => setShowConversionModal(true)}
                disabled={convertLeadMutation.isPending}
                className={`p-2 rounded-lg transition-colors ${
                  convertLeadMutation.isPending
                    ? 'bg-white/20 text-white/60 cursor-not-allowed'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                title="Convert to Client"
              >
                <UserPlus2Icon className="w-5 h-5" />
              </button>
            )
          )}
        </div>

        {(showClientStatusControls || showLeadTagControls) && (
          <div className="space-y-3">
            {showClientStatusControls && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-white/80">
                  Client Status
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 rounded-full bg-white/10 p-1">
                    {clientStatusOptions.map((option) => {
                      const isActive = displayedClientStatus === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            isActive
                              ? 'bg-white text-[#5B8E87] shadow'
                              : 'text-white/80'
                          } ${clientControlsDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white/20'}`}
                          onClick={() => handleClientStatusChange(option.value)}
                          disabled={clientControlsDisabled}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  {clientStatusMutation.isPending && (
                    <span className="text-xs text-white/80">Updating…</span>
                  )}
                </div>
                {statusError && (
                  <span className="text-xs text-red-100">{statusError}</span>
                )}
              </div>
            )}

            {showLeadTagControls && jid && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide font-semibold text-white/80">
                  Lead Tag
                </span>
                <div className="flex flex-wrap gap-2">
                  {leadLabelOptions.map((option) => {
                    const isActive = activeLeadLabel === option.value;
                    const activeClasses = isActive
                      ? leadLabelActiveStyles[option.value]
                      : 'bg-white/10 text-white/80 border border-white/30';
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${activeClasses} ${
                          leadControlsDisabled
                            ? 'opacity-70 cursor-not-allowed'
                            : isActive
                            ? ''
                            : 'hover:bg-white/20'
                        }`}
                        onClick={() => handleLeadLabelChange(option.value)}
                        disabled={leadControlsDisabled}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                {(leadTagLoading || leadLabelMutation.isPending) && (
                  <span className="text-xs text-white/80">
                    {leadTagLoading ? 'Loading lead tag…' : 'Saving lead tag…'}
                  </span>
                )}
                {leadLabelError && (
                  <span className="text-xs text-red-100">{leadLabelError}</span>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading && <p className="text-center text-gray-500">Loading conversation...</p>}
        {!isLoading && messages.length === 0 && (
          <p className="text-center text-gray-500">No messages yet.</p>
        )}
        {!isLoading && groupedMessages.map((group) => (
          <div key={group.date.toISOString()} className="space-y-3">
            {/* Date divider */}
            <div className="flex justify-center my-4">
              <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-lg shadow-sm">
                <span className="text-xs font-medium text-gray-600">{group.dateLabel}</span>
              </div>
            </div>

            {/* Messages for this date */}
            {group.messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.direction === 'outgoing' ? 'justify-end' : 'justify-start'} group`}
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
              >
                <div className="relative flex items-start gap-1">
                  {/* Message menu button for incoming messages - on the left */}
                  {message.direction === 'incoming' && (
                    <button
                      onClick={() => {
                        setSelectedMessageForNote({
                          id: message.id,
                          text: message.text,
                          timestamp: new Date(message.timestamp).toLocaleString(),
                          media_url: message.media_url,
                          media_type: message.media_type,
                        });
                        setShowAddNoteModal(true);
                      }}
                      className={`
                        flex-shrink-0 p-1.5 rounded-full transition-all
                        bg-gray-100 hover:bg-gray-200
                        ${hoveredMessageId === message.id ? 'opacity-100' : 'opacity-0 md:opacity-0'}
                        group-hover:opacity-100
                        active:scale-95
                        mt-1
                      `}
                      aria-label="Save message to notes"
                      title="Save to notes"
                    >
                      <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  )}

                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow ${
                      message.direction === 'outgoing'
                        ? 'bg-[#DCF8C6] text-[#212121]'
                        : 'bg-white text-[#212121]'
                    }`}
                  >
                  {/* Render media if present */}
                  {message.mediaUrl && message.mediaType === 'image' && (
                    <div className="relative mb-2">
                      {imageLoadErrors.has(message.id) ? (
                        <div className="bg-gray-100 rounded-lg p-4 text-center">
                          <p className="text-gray-500 text-sm">Failed to load image</p>
                        </div>
                      ) : imageBlobUrls.has(message.id) ? (
                        <img
                          src={imageBlobUrls.get(message.id)!}
                          alt="WhatsApp image"
                          className="rounded-lg max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setLightboxImage(imageBlobUrls.get(message.id)!)}
                        />
                      ) : (
                        <div className="bg-gray-100 rounded-lg p-4 text-center">
                          <p className="text-gray-500 text-sm">Loading image...</p>
                        </div>
                      )}
                    </div>
                  )}
                  {message.mediaUrl && message.mediaType === 'video' && (
                    <video
                      src={message.mediaUrl}
                      controls
                      className="rounded-lg mb-2 max-w-full"
                    />
                  )}
                  {message.mediaUrl && message.mediaType === 'audio' && (
                    <audio
                      src={message.mediaUrl}
                      controls
                      className="mb-2 w-full"
                    />
                  )}
                  {message.mediaUrl && message.mediaType === 'document' && (
                    <a
                      href={message.mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mb-2 text-blue-600 hover:underline"
                    >
                      📄 Download Document
                    </a>
                  )}
                  <p className="whitespace-pre-wrap"><LinkifiedText text={message.text} /></p>

                  {/* Reaction Display */}
                  {message.reactions && message.reactions.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {(() => {
                        // Aggregate reactions by emoji
                        const reactionCounts = message.reactions.reduce((acc, reaction) => {
                          const emoji = reaction.emoji;
                          if (!acc[emoji]) {
                            acc[emoji] = {
                              count: 0,
                              users: []
                            };
                          }
                          acc[emoji].count++;
                          acc[emoji].users.push(reaction.fromName);
                          return acc;
                        }, {} as Record<string, { count: number; users: string[] }>);

                        return Object.entries(reactionCounts).map(([emoji, data]) => (
                          <span
                            key={emoji}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              message.direction === 'outgoing'
                                ? 'bg-[#B8E994] text-gray-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                            title={data.users.join(', ')}
                          >
                            <span>{emoji}</span>
                            {data.count > 1 && <span className="text-xs">{data.count}</span>}
                          </span>
                        ));
                      })()}
                    </div>
                  )}

                  {/* Reaction Picker */}
                  {showReactionPicker === message.id && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {quickReactions.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleReaction(message.id, emoji)}
                          className="text-2xl p-2 hover:bg-gray-200 rounded-full transition-all active:scale-95"
                          title={`React with ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-1">
                    <button
                      onClick={() => setShowReactionPicker(showReactionPicker === message.id ? null : message.id)}
                      className={`text-xs transition-all ${
                        hoveredMessageId === message.id || showReactionPicker === message.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      } text-gray-400 hover:text-gray-600`}
                      title="Add reaction"
                    >
                      {showReactionPicker === message.id ? '✕' : '😊'}
                    </button>
                    <div className="text-[10px] text-gray-500">
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {message.status === 'error' && <span className="text-red-500 ml-1">Failed</span>}
                    </div>
                  </div>
                </div>

                {/* Message menu button for outgoing messages - on the right */}
                {message.direction === 'outgoing' && (
                  <button
                    onClick={() => {
                      setSelectedMessageForNote({
                        id: message.id,
                        text: message.text,
                        timestamp: new Date(message.timestamp).toLocaleString(),
                        media_url: message.media_url,
                        media_type: message.media_type,
                      });
                      setShowAddNoteModal(true);
                    }}
                    className={`
                      flex-shrink-0 p-1.5 rounded-full transition-all
                      bg-white/60 hover:bg-white/80
                      ${hoveredMessageId === message.id ? 'opacity-100' : 'opacity-0 md:opacity-0'}
                      group-hover:opacity-100
                      active:scale-95
                      mt-1
                    `}
                    aria-label="Save message to notes"
                    title="Save to notes"
                  >
                    <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>
            </div>
            ))}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="bg-white p-3 border-t border-gray-200 flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => setShowTemplatePicker(true)}
          disabled={composerDisabled || loadingTemplate}
          className={`p-3 rounded-full ${
            composerDisabled || loadingTemplate
              ? 'text-gray-300'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          title="Use Template"
        >
          {loadingTemplate ? (
            <RefreshCwIcon className="w-5 h-5 animate-spin" />
          ) : (
            <FileTextIcon className="w-5 h-5" />
          )}
        </button>
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            className="chat-textarea w-full resize-none rounded-2xl border border-gray-200 px-4 py-2 text-base leading-6 focus:outline-none focus:ring-2 focus:ring-[#5B8E87] overflow-y-auto"
            placeholder={composerDisabled ? 'WhatsApp disconnected' : 'Type a message'}
            value={draft}
            disabled={composerDisabled}
            onChange={(event) => setDraft(event.target.value)}
            aria-label="Type a message"
            aria-multiline="true"
            role="textbox"
          />
          {isScrollable && (
            <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-white/80 to-transparent rounded-b-2xl" />
          )}
        </div>
        <button
          onClick={handleSend}
          disabled={composerDisabled || !draft.trim()}
          className={`p-3 rounded-full text-white ${
            composerDisabled || !draft.trim()
              ? 'bg-gray-300'
              : 'bg-[#5B8E87] hover:bg-[#4F7A74]'
          }`}
        >
          <SendHorizontalIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <TemplatePickerModal
          onSelect={async (templateId) => {
            setShowTemplatePicker(false);
            setLoadingTemplate(true);

            try {
              // Fetch the template using api client (handles auth automatically)
              const { data: template } = await api.get<any>(`/api/templates/${templateId}`);

              // Find the current client to get their data
              const currentClient = clientsQuery.data?.find(c => c.whatsapp_jid === jid);

              if (currentClient) {
                // Generate variables from client data
                const firstName = currentClient.name?.split(' ')[0] || currentClient.name;
                const currentDate = new Date().toLocaleDateString('en-US', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                });
                const currentTime = new Date().toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                });

                const variables = {
                  name: currentClient.name,
                  first_name: firstName,
                  phone: currentClient.phone,
                  goal: currentClient.goal || '',
                  date: currentDate,
                  time: currentTime,
                };

                // Render the template text
                const textBlocks = template.content.blocks
                  .filter((b: any) => b.type === 'text')
                  .map((b: any) => b.content);

                let renderedText = textBlocks.join('\n\n');

                // Replace variables
                const variablePattern = /\{\{([a-zA-Z0-9_]+)\}\}/g;
                renderedText = renderedText.replace(variablePattern, (match: string, varName: string) => {
                  const value = variables[varName as keyof typeof variables];
                  return value !== undefined && value !== null ? String(value) : '';
                });

                // Set the rendered text in the draft
                setDraft(renderedText);
              } else {
                // No client found, just use the template as-is
                const textBlocks = template.content.blocks
                  .filter((b: any) => b.type === 'text')
                  .map((b: any) => b.content);
                setDraft(textBlocks.join('\n\n'));
              }
            } catch (error) {
              console.error('Error loading template:', error);
              alert('Failed to load template. Please try again.');
            } finally {
              setLoadingTemplate(false);
            }
          }}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      <LeadConversionModal
        open={showConversionModal}
        defaultName={preferredName}
        defaultPhone={state.clientPhone ?? ''}
        onClose={closeConversionModal}
        onConvert={handleConvertLead}
        isSubmitting={convertLeadMutation.isPending}
        error={conversionError}
      />

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageViewer
          imageUrl={lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {/* Add Note Modal */}
      {showAddNoteModal && selectedMessageForNote && (
        <AddNoteModal
          message={{
            id: selectedMessageForNote.id,
            text: selectedMessageForNote.text,
            timestamp: selectedMessageForNote.timestamp,
            isOutgoing: false,
            media_url: selectedMessageForNote.media_url,
            media_type: selectedMessageForNote.media_type,
          }}
          clientId={resolvedClientId}
          onClose={() => {
            setShowAddNoteModal(false);
            setSelectedMessageForNote(null);
          }}
          onSuccess={() => {
            // Optional: Show a toast notification
            console.log('Note saved successfully');
          }}
        />
      )}
    </div>
  );
}
